import { FetchError } from 'ofetch'

import { AsyncDataKeys } from '@/constants/async-data-keys'
import { getPromoCodes, receivePromoCode } from '@/service/promo'

import { useAlertStore } from '@/store/alert'

import type { FpromoCodeData } from '@/types/api'
import { GtmEvent } from '@/types/gtm'
import { ResponseCode } from '@/types/service'

export function useGetPromoCodes() {
  const {
    data: promosResp,
    refresh: refreshPromoCodes,
    status: getPromoCodesStatus,
  } = useAsyncData(useAsyncDataKey(AsyncDataKeys.PROMO_CODES, []), getPromoCodes, { server: false })

  const promoCodes = computed(() => promosResp.value?.data)
  const isPromoCodesLoading = computed(() => ['idle', 'pending'].includes(getPromoCodesStatus.value))

  return { promoCodes, refreshPromoCodes, isPromoCodesLoading, getPromoCodesStatus }
}

export type UseReceivePromoCodeExecutorParams = {
  onSuccess?: (data: FpromoCodeData) => Promise<void> | void
}

export function useReceivePromoCode({ defaultPromoCode = '' } = {}) {
  const nuxtApp = useNuxtApp()

  const alertStore = useAlertStore()

  const { t } = useI18n()

  const { isLoading, execute } = useAsyncState(executor, {} as any, { immediate: false })

  const promoCode = ref(defaultPromoCode)
  const receivePromoError = ref('')

  watch(promoCode, () => {
    if (receivePromoError.value) {
      receivePromoError.value = ''
    }
  })

  async function executor(params: UseReceivePromoCodeExecutorParams) {
    const { onSuccess } = params

    receivePromoError.value = ''

    try {
      const { data } = await receivePromoCode({ promo_code: promoCode.value })

      if (typeof onSuccess === 'function') {
        await onSuccess(data)
      }
      alertStore.success(t('promo.toast.claimed'))
      nuxtApp.$trackEvent({
        event: GtmEvent.PROMO_CODE_CLAIMED,
        payload: {
          coupon: data.promo_code,
        },
      })
      promoCode.value = ''
    } catch (error: any) {
      if (error instanceof FetchError) {
        const code = error.data?.code

        switch (code) {
          case ResponseCode.PROMO_DUPLICATE:
            receivePromoError.value = t('promo.input_error.duplicated')
            return
          case ResponseCode.PROMO_LIMIT_MEMBER_TAG:
            receivePromoError.value = t('promo.input_error.not_eligible')
            return
          case ResponseCode.PROMO_LIMIT_GROUP:
          case ResponseCode.PROMO_LIMIT_CNT:
            receivePromoError.value = t('promo.input_error.exceed_limit')
            return
          case ResponseCode.PROMO_INPUT_TIME_EXPIRED:
            receivePromoError.value = error.data?.message || error.message || ''
            return
          case ResponseCode.UNREGISTERED:
          default:
            receivePromoError.value = t('promo.input_error.invalid_code')
            return
        }
      }
      alertStore.error(error.data?.message || error.message || '')
    }
  }

  return { promoCode, isLoading, receivePromoError, executeReceivePromo: execute.bind(null, 0) }
}
